import
  React, {
  useContext,
  useEffect,
  useState
} from 'react';

import {
  useHistory,
  useLocation
} from 'react-router';

import {
  ColorCodes,
  ColorSvg,
  HContainerH,
  JourneyProvider,
  Text,
  VContainer,
  VContainerH,
  VContainerVH,
  VFillContainer
} from '@vodacom/web-common';

import {
  AAPromptStates,
  AdvanceType,
  CacheKeys,
  Icons,
  OtpType
} from '../../../../constants';

import {
  AdvanceProvider
} from '../../providers';

import {
  Modal,
  ModalCard,
  PrimaryButton,
  SecondaryButton
} from 'components';

import {
  HandleOtp,
  withScreenViewTagging
} from 'hocs';

import {
  getStorageWithExpiry,
  isEqual,
  useRedirectHandler
} from 'helpers';

import {
  AppContext
} from 'context';

const AirtimeAdvanceSummaryUI = props => {

  const history = useHistory();
  const redirectHandler = useRedirectHandler();

  const {
    promptState,
    setPromptState
  } = useContext(AppContext);

  const location = useLocation();

  const urlParams = new URLSearchParams(location?.search);

  const advance_id = urlParams.get('advance_id');
  const advance_type = urlParams.get('advance_type');

  const [ state, setState ] = useState({
    loading: true,
    provisionLoading: false,
    error: null,
    showModal: false,
    showOTPModal: false,
    advance: undefined,
    successMessage: null,
    errorMessage: 'A technical error has occurred.'
  });

  useEffect(() => {

    if (!state.advance) {

      const init = async () => {

        await loadInfo();
      };

      init();

    }
  }, []);

  const loadInfo = async () => {

    const response = await AdvanceProvider.getCachedItem(CacheKeys.AirtimeAdvanceOffers, null, AdvanceProvider.getAvailableOffers);

    if (!response.ok) {
      setState(prevState => ({
        ...prevState,
        loading: false,
        error: response?.error
      }));

      return;
    }

    const advance = response.result?.airtimeBands?.filter(x => x.id === advance_id)[0]
      || response.result?.dataBands?.filter(x => x.id === advance_id)[0]
      || response.result?.voiceBands?.filter(x => x.id === advance_id)[0];

    if (!advance) {
      history.goBack();
    }

    AirtimeAdvanceSummaryUI.tagViewData = {
      ...AirtimeAdvanceSummaryUI.tagViewData,
      product_price: advance?.serviceFee?.value,
      product_id: [ advance_id ]
    };

    const requestOfferType = isEqual(advance.type, AdvanceType.Airtime) ? 'an Airtime' : isEqual(advance.type, AdvanceType.Voice) ? 'a Voice' : 'a Data';

    const useOfferType = isEqual(advance.type, AdvanceType.Airtime) ? 'airtime' : isEqual(advance.type, AdvanceType.Voice) ? 'voice bundle': 'data';

    // TODO: Add this to config
    setState(prevState => ({
      ...prevState,
      loading: false,
      advance: advance,
      successMessage: `We've received your request for ${requestOfferType} advance. You'll receive an SMS once your ${useOfferType} is available for use.\n\nPlease note: You have 30 days to pay the advance.`
    }));
  };

  const requestOtp = async (response) => {

    if (response?.code === 423 && response?.result) {

      setState(prevState => ({
        ...prevState,
        error: true,
        errorMessage: response?.result?.messages?.[0]?.message,
        showOTPModal: false,
        showModal: true
      }));

      return;
    }

    if (!response?.ok || !response?.result) {

      setState(prevState => ({
        ...prevState,
        error: true,
        errorMessage: response?.result?.message || 'There was a problem sending your OTP. Please try again later or call 082 135',
        showOTPModal: false,
        showModal: true
      }));

      return;
    }
  };

  const resendOtp = async (response) => {

    if (response?.code === 423 && response?.result) {

      setState(prevState => ({
        ...prevState,
        error: true,
        errorMessage: response?.result?.messages?.[0]?.message,
        showOTPModal: false,
        showModal: true
      }));

      return;
    }

    if (!response?.ok || !response?.result) {

      setState(prevState => ({
        ...prevState,
        error: true,
        errorMessage: response?.result?.message || 'There was a problem sending your OTP. Please try again later or call 082 135',
        showOTPModal: false,
        showModal: true
      }));

      return;
    }

    if (response?.ok && response?.result) {

      window?.utag?.view(
        {
          page_name: 'cbu: connectu: airtime advance: resent otp',
          page_section: 'airtime advance',
          visitor_type: 'consumer',
          visitor_customer_segment: getStorageWithExpiry('msisdn', 'msisdnType', ''),
          visitor_id_asset_active: getStorageWithExpiry('msisdn', 'hashedMsisdn', '')
        }
      );
    }
  };

  const provisionLoan = async (
    advanceType,
    advanceId
  ) => {

    setState(prevState => ({
      ...prevState,
      provisionLoading: true,
      showOTPModal: false,
      showModal: true
    }));

    const loanResponse = await AdvanceProvider.provisionLoan(advanceType, advanceId);

    if (loanResponse?.ok && loanResponse?.result) {

      setState(prevState => ({
        ...prevState,
        customOrderId: loanResponse.result?.customOrderId
      }));
    }

    if (!loanResponse?.ok || !loanResponse?.result) {

      setState(prevState => ({
        ...prevState,
        error: true,
        errorMessage: 'There was a problem purchasing your advance. Please try again later or call 082 135'
      }));
    }

    setState(prevState => ({
      ...prevState,
      provisionLoading: false
    }));

    AdvanceProvider.deleteCachedItem(CacheKeys.AirtimeAdvanceOffers);
    AdvanceProvider.deleteCachedItem(CacheKeys.AirtimeAdvanceBalances);
    AdvanceProvider.deleteCachedItem(CacheKeys.AirtimeAdvanceHistory);
  };

  const verifyOtp = async response => {

    if (response?.ok && response?.result) {

      await provisionLoan(advance_type, advance_id);
      return;
    }

    if ([ 422, 423 ].includes(response?.code)) {

      window?.utag?.view(
        {
          page_event: 'page_error',
          page_section: 'airtime advance',
          error_message: response?.error
        }
      );

      return;
    }

    if (!response?.ok || !response?.result) {

      setState(prevState => ({
        ...prevState,
        error: true,
        errorMessage: 'There was a problem purchasing your advance. Please try again later or call 082 135',
        showOTPModal: false,
        showModal: true
      }));
    }
  };

  const buildModalCard = () => {

    return (

      <ModalCard
        padding={'22px 34px 33px'}
        isSuccess={!state.error}
        iconHeight={'80px'}
        iconWidth={'80px'}
        title={!state.error ? 'Success' : 'Something went wrong.'}
        titleFontSize={'26px'}
        message={!state.error ? state.successMessage : state.errorMessage}
        messageFontFamily={'Roboto'}
        messageWeight={'normal'}
        messageFontSize={'14px'}
        buttonText={'Okay'}
        isLoading={state.provisionLoading}
        onClose={() => {

          if (promptState === AAPromptStates.Started) {

            setPromptState(AAPromptStates.Finished);

            redirectHandler(
              getStorageWithExpiry('Prompt', 'returnUrl'),
              JourneyProvider.get('Prompt', 'url'),
              false,
              true
            );

            return;
          }

          setState(prevState => ({
            ...prevState,
            showModal: false
          }));

          history.goBack();

        }}
        pageSection={'airtime advance'}
        pageName= {'cbu: connectu: airtime advance: purchase complete'}
        tagViewData={{
          ...AirtimeAdvanceSummaryUI.tagViewData,
          journey_event: 'journey_end',
          journey_step: 'cbu: connectu: airtime advance: purchase complete',
          journey_type: 'just for you',
          sales_event: 'transaction_complete, airtimeadvance',
          transaction_id: state.customOrderId
        }} />
    );
  };

  const buildInfoIcon = () => {

    return (

      <VContainerVH
        flex={'1'}
        margin={'16px 0 38px'} >

        <ColorSvg
          src={Icons.InfoCircle}
          color={ColorCodes.Grey11}
          height={'67px'}
          width={'67px'} />
      </VContainerVH>
    );
  };

  const buildCancelButton = () => {

    return (

      <SecondaryButton
        margin={'16px 0 0'}
        border={'2px solid'}
        text={props.cancelText}
        onClick={() => history.goBack()}/>
    );
  };

  const buildConfirmButton = () => {

    return (

      <PrimaryButton
        text={props.continueText}
        onClick={() => {

          setState(prevState => ({
            ...prevState,
            showOTPModal: true
          }));
        }}/>
    );
  };

  const buildButtons = () => {

    return (

      <VContainerH >

        { buildConfirmButton() }

        { buildCancelButton() }

      </VContainerH>
    );
  };

  const buildPaymentInfo = () => {

    return (

      <VFillContainer
        margin={'16px 0'}
        justifyContent={'start'}
        color={ColorCodes.Grey11} >

        <HContainerH >

          <Text
            flex={'1'} >

            {props.feeLabel}
          </Text>

          <Text >

            {state.advance?.serviceFee?.displayValue}
          </Text>

        </HContainerH>

        <HContainerH
          margin={'12px 0 14px'} >

          <Text
            flex={'1'}
            fontWeight={'bold'} >

            {props.totalLabel}
          </Text>

          <Text
            fontWeight={'bold'} >

            {state.advance?.totalRepaymentValue?.displayValue}
          </Text>

        </HContainerH>

        <HContainerH >

          <Text
            flex={'1'}
            fontSize={'14px'} >

            {props.clause}
          </Text>

        </HContainerH>

      </VFillContainer>
    );
  };

  const buildOTPModalCard = () => {

    const onModalClose = () => {

      setState(prevState => ({
        ...prevState,
        showOTPModal: false
      }));
    };

    return (

      <>
        <Modal/>

        <HandleOtp
          onRequestOtp={response => requestOtp(response)}
          onResendOtp={response => resendOtp(response)}
          onVerifyOtp={response => verifyOtp(response)}
          otpType={OtpType.Otp}
          onClose={onModalClose}
          pageName={'cbu: connectu: airtime advance: enter otp'}
          tagViewData={{
            visitor_customer_segment: getStorageWithExpiry('msisdn', 'msisdnType', ''),
            visitor_id_asset_active: getStorageWithExpiry('msisdn', 'hashedMsisdn', ''),
            page_section: 'airtime advance',
            visitor_type: 'consumer'
          }}/>

      </>

    );
  };

  return (

    <>
      { state.advance &&

        <VFillContainer
          height={'100vh'}
          padding={'15px 23px 23px'}
          boxSizing={'border-box'} >

          { state.showOTPModal && buildOTPModalCard() }

          { state.showModal && buildModalCard() }

          <VContainer
            borderBottom={`1px solid ${ColorCodes.Grey4}`}
            justifyContent={'flex-end'} >

            {buildInfoIcon()}

            <Text
              textAlign={'center'}
              color={ColorCodes.Grey9} >

              {props.title}
            </Text>

            <Text
              textAlign={'center'}
              padding={'8px 0 16px'}
              fontSize={'26px'}
              fontWeight={'bold'}
              color={ColorCodes.Grey11} >

              {state.advance?.displayValue}
            </Text>

          </VContainer>

          {buildPaymentInfo()}

          <VFillContainer
            justifyContent={'flex-end'}
            margin='72px 0' >

            <Text
              textAlign={'center'}
              color={ColorCodes.Grey9} >

              {props.disclaimer}
            </Text>

            <Text
              textDecoration={'underline'}
              textAlign={'center'}
              color={ColorCodes.Blue1}
              onClick={() => {
                history.push(`${props.legalPath}`);
              }} >

              {props.legalText}
            </Text>

          </VFillContainer>

          {buildButtons()}

        </VFillContainer>
      }
    </>

  );
};

AirtimeAdvanceSummaryUI.defaultProps = {
  title: 'Summary',
  feeLabel: 'Access fee:',
  totalLabel: 'Total repayment',
  clause: 'Payable the next time you recharge',
  disclaimer: 'By selecting "Confirm" you agree to the',
  legalText: 'terms and conditions',
  legalPath: '/advance/legal',
  cancelText: 'Cancel',
  cancelPath: '/advance',
  continueText: 'Confirm'
};

AirtimeAdvanceSummaryUI.pageName = 'cbu: connectu: airtime advance: purchase summary';

AirtimeAdvanceSummaryUI.tagViewData = {
  journey_step: 'cbu: connectu: airtime advance: purchase summary',
  journey_name: 'cbu: connectu: airtime advance: purchase',
  journey_type: 'airtime advance',
  sales_event: 'product_view',
  product_category: 'Airtime Advance',
  product_quantity: '1',
  product_price: 'Service Fee',
  customer_city: getStorageWithExpiry('Location', 'city', null),
  visitor_customer_segment: getStorageWithExpiry('msisdn', 'msisdnType', ''),
  visitor_id_asset_active: getStorageWithExpiry('msisdn', 'hashedMsisdn', ''),
  page_section: 'airtime advance',
  visitor_type: 'consumer'
};

export const AirtimeAdvanceSummary = withScreenViewTagging(AirtimeAdvanceSummaryUI);
